<script setup>
import MarketNoteMark from 'components/market/MarketNoteMark.vue';
import { formatMarketMetric } from 'src/utils/market-util';
import { computed } from 'vue';

const PLOT_WIDTH = 1000;
const PLOT_HEIGHT = 100;
const BAR_GAP = 3;
const LABEL_SPACING = 110;
const QUARTILE_LABEL_CLEARANCE = 360;

const $props = defineProps({
    note: {
        type: Number,
        default: 2
    },
    salary: {
        type: Object,
        default: null
    }
});

const bands = computed(() => Array.isArray($props.salary?.bands) ? $props.salary.bands : []);

const isDrawable = computed(() => {
    return $props.salary?.isSampleSufficient === true && bands.value.length > 0
        && [$props.salary.median, $props.salary.p25, $props.salary.p75].every(Number.isFinite);
});

const domain = computed(() => {
    const first = bands.value[0];
    const last = bands.value[bands.value.length - 1];
    const previousWidth = bands.value.length > 1
        ? last.min - bands.value[bands.value.length - 2].min
        : last?.min || 50000;
    return {
        min: first?.min ?? 0,
        max: Number.isFinite(last?.max) ? last.max : (last?.min ?? 0) + previousWidth
    };
});

// The bands are not equally wide, so a band's height is its share of listings for each dollar of pay it spans. Drawn
// by share alone, the widest band would be the tallest simply for gathering more of the range.
const bars = computed(() => {
    const densities = bands.value.map(band => (band.percent || 0) / bandSpan(band));
    const largestDensity = Math.max(Number.EPSILON, ...densities);
    return bands.value.map((band, index) => {
        const x = scale(band.min);
        const width = Math.max(1, scale(bandEnd(band)) - x - BAR_GAP);
        const height = Math.round(PLOT_HEIGHT * densities[index] / largestDensity);
        return { key: band.min, x, width, height, y: PLOT_HEIGHT - height };
    });
});

const markers = computed(() => {
    const lower = buildMarker($props.salary?.p25, 'Lower quartile');
    const upper = buildMarker($props.salary?.p75, 'Upper quartile');
    if (upper.x - lower.x < QUARTILE_LABEL_CLEARANCE) {
        lower.style = positionLabel(lower.x, 'end');
        upper.style = positionLabel(upper.x, 'start');
    }
    return { lower, upper, median: buildMarker($props.salary?.median, 'Median') };
});

const axisLabels = computed(() => {
    const lastBand = bands.value[bands.value.length - 1];
    const labels = [];
    bands.value.forEach(band => {
        const x = scale(band.min);
        if (labels.length === 0 || x - labels[labels.length - 1].x >= LABEL_SPACING) {
            const isOpenEnded = band === lastBand && !Number.isFinite(band.max);
            labels.push({ x, label: `${formatAxisValue(band.min)}${isOpenEnded ? '+' : ''}` });
        }
    });
    return labels;
});

const description = computed(() => {
    if (!isDrawable.value) {
        return null;
    }
    return `Advertised annual salary: median ${formatSalary($props.salary.median)}, middle half `
        + `${formatSalary($props.salary.p25)} to ${formatSalary($props.salary.p75)}, `
        + `from ${formatMarketMetric($props.salary.sampleSize)} listings with usable pay.`;
});

// The threshold is named by the snapshot rather than restated here, so the copy cannot outlive a change to the rule.
const withheldMessage = computed(() => {
    if (!$props.salary) {
        return 'Advertised pay has not been measured for this snapshot.';
    }
    const observed = `(${formatMarketMetric($props.salary.sampleSize || 0)} observed)`;
    return Number.isFinite($props.salary.minimumSampleSize)
        ? `Salary bands are withheld while fewer than ${$props.salary.minimumSampleSize} listings carry usable pay ${observed}.`
        : `Salary bands are withheld until enough listings carry usable pay ${observed}.`;
});

function bandEnd(band) {
    return Number.isFinite(band.max) ? band.max : domain.value.max;
}

function bandSpan(band) {
    return Math.max(1, bandEnd(band) - band.min);
}

function scale(value) {
    const span = domain.value.max - domain.value.min || 1;
    const ratio = (value - domain.value.min) / span;
    return Math.round(Math.min(Math.max(ratio, 0), 1) * PLOT_WIDTH);
}

function buildMarker(value, title) {
    const x = scale(value);
    return {
        x,
        title,
        label: formatSalary(value),
        style: positionLabel(x)
    };
}

function positionLabel(x, anchor = null) {
    if (anchor === 'start' || (anchor === null && x < 140)) {
        return { left: `calc(${x / 10}% + 6px)`, transform: 'translateX(0)' };
    }
    if (anchor === 'end' || (anchor === null && x > PLOT_WIDTH - 140)) {
        return { left: `calc(${x / 10}% - 6px)`, transform: 'translateX(-100%)' };
    }
    return { left: `${x / 10}%`, transform: 'translateX(-50%)' };
}

function formatSalary(value) {
    return formatMarketMetric(value, { type: 'salary', currency: $props.salary?.currency || 'AUD' });
}

function formatAxisValue(value) {
    return value >= 1000 ? `$${Math.round(value / 1000)}k` : `$${value}`;
}
</script>

<template>
    <figure class="market-salary-strip q-ma-none">
        <figcaption class="row items-baseline justify-between q-gutter-x-md q-mb-sm">
            <span class="text-body2 text-weight-medium">
                Advertised annual salary, {{ $props.salary?.currency || 'AUD' }}<MarketNoteMark :number="$props.note"/>
            </span>
            <span v-if="isDrawable" class="font-mono text-caption text-hint">
                n = {{ formatMarketMetric($props.salary.sampleSize) }} listings with usable pay
            </span>
        </figcaption>
        <template v-if="isDrawable">
            <div aria-hidden="true" class="strip-median relative-position font-mono text-caption text-weight-bold">
                <span :style="markers.median.style" class="absolute text-no-wrap">
                    {{ markers.median.title }} {{ markers.median.label }}
                </span>
            </div>
            <svg
                :aria-label="description"
                :viewBox="`0 0 ${PLOT_WIDTH} ${PLOT_HEIGHT}`"
                class="strip-plot text-primary"
                preserveAspectRatio="none"
                role="img"
            >
                <rect
                    v-for="bar in bars"
                    :key="bar.key"
                    :height="bar.height"
                    :width="bar.width"
                    :x="bar.x"
                    :y="bar.y"
                    class="strip-bar"
                    data-test="salary-band"
                    fill="currentColor"
                />
                <line
                    :x1="markers.lower.x"
                    :x2="markers.lower.x"
                    :y2="PLOT_HEIGHT"
                    data-test="salary-p25"
                    stroke="currentColor"
                    stroke-dasharray="4 4"
                    stroke-width="1.5"
                    vector-effect="non-scaling-stroke"
                    y1="0"
                />
                <line
                    :x1="markers.upper.x"
                    :x2="markers.upper.x"
                    :y2="PLOT_HEIGHT"
                    data-test="salary-p75"
                    stroke="currentColor"
                    stroke-dasharray="4 4"
                    stroke-width="1.5"
                    vector-effect="non-scaling-stroke"
                    y1="0"
                />
                <line
                    :x1="markers.median.x"
                    :x2="markers.median.x"
                    :y2="PLOT_HEIGHT"
                    data-test="salary-median"
                    stroke="var(--q-accent)"
                    stroke-width="3"
                    vector-effect="non-scaling-stroke"
                    y1="0"
                />
                <line
                    :x2="PLOT_WIDTH"
                    :y1="PLOT_HEIGHT"
                    :y2="PLOT_HEIGHT"
                    stroke="currentColor"
                    stroke-width="1"
                    vector-effect="non-scaling-stroke"
                    x1="0"
                />
            </svg>
            <div aria-hidden="true" class="strip-axis relative-position font-mono text-caption text-hint">
                <span v-for="tick in axisLabels" :key="tick.label" :style="{ left: `${tick.x / 10}%` }" class="absolute">
                    {{ tick.label }}
                </span>
            </div>
            <div aria-hidden="true" class="strip-quartiles relative-position gt-xs font-mono text-caption text-hint">
                <span :style="markers.lower.style" class="absolute text-no-wrap">
                    {{ markers.lower.title }} {{ markers.lower.label }}
                </span>
                <span :style="markers.upper.style" class="absolute text-no-wrap">
                    {{ markers.upper.title }} {{ markers.upper.label }}
                </span>
            </div>
            <div aria-hidden="true" class="row justify-between lt-sm q-mt-xs font-mono text-caption text-hint">
                <span>{{ markers.lower.title }} {{ markers.lower.label }}</span>
                <span>{{ markers.upper.title }} {{ markers.upper.label }}</span>
            </div>
            <p class="font-mono text-caption text-hint q-mt-sm q-mb-none">Bar height shows listings per $10k of pay.</p>
        </template>
        <p v-else class="text-body2 text-hint q-my-none" role="status">{{ withheldMessage }}</p>
    </figure>
</template>

<style lang="scss" scoped>
.market-salary-strip {
    .strip-median {
        color: $orange-10;
        height: 22px;
    }

    .strip-plot {
        display: block;
        height: 100px;
        width: 100%;

        @media (prefers-reduced-motion: no-preference) {
            animation: market-strip-rise 600ms cubic-bezier(0.2, 0.7, 0.2, 1) both;
        }
    }

    // Opaque enough for each bar to hold 3:1 against the page, which a graphic has to carry to be read.
    .strip-bar {
        opacity: 0.7;
    }

    .strip-axis,
    .strip-quartiles {
        height: 20px;
    }

    .strip-axis span {
        top: 2px;
    }

    .strip-quartiles span {
        top: 0;
    }

    .body--dark & {
        .strip-median {
            color: $accent;
        }

        .strip-bar {
            fill: var(--link-color);
            opacity: 0.8;
        }
    }
}

@keyframes market-strip-rise {
    from {
        opacity: 0;
        transform: translateY(8px);
    }
}
</style>
